import type { ExternalTableSchema } from '@packages/table-builder';
import { IMPORT_EXPORT_SEMANTIC_IDS } from '@packages/table-builder/utils/importExportSemanticIds';

export const optionAwardsTable: ExternalTableSchema = {
  metadata: {
    title: 'Option Awards',
    entity: 'Option Awards',
  },

  dataSource: {
    endpoint: '/api/award/api/app/option-award/option-award-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },
    staticQueryParams: {
      OptionStatus: 0,
      ApprovalStatusIds: [0],
      EmployeeStatuses: [0],
    },
    filters: [
      {
        name: 'CreatedFromDate',
        placeholder: 'Created Date From',
        type: 'date',
      },
      {
        name: 'CreatedToDate',
        placeholder: 'Created Date To',
        type: 'date',
      },
      {
        name: 'ApprovalStatusIds',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/option-award/option-award-statuses',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'OptionAcceptance',
        placeholder: 'Accept Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/option-award/option-acceptances',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'GrantFromDate',
        placeholder: 'Grant Date From',
        type: 'date',
      },
      {
        name: 'GrantToDate',
        placeholder: 'Grant Date To',
        type: 'date',
      },
    ],
  },

  columns: [
    { field: 'grantNo', header: 'Award #', type: 'text', sortable: true, width: 150 },
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      couple: { titleField: 'participantName', subtitleField: 'employeeId' },
    },
    { field: 'issueDate', header: 'Grant Date', type: 'date', sortable: true, width: 150, align: 'center' },
    { field: 'awardType', header: 'Award Type', type: 'text', sortable: true, width: 100, align: 'center' },
    {
      field: 'awardStatus',
      header: 'Award Status',
      type: 'badge',
      sortable: false,
      width: 100,
      align: 'center',
      badge: {
        statusMap: {
          'Fully Vested': 'success',
          Expired: 'error',
          Rejected: 'error',
          Cancelled: 'error',
          Outstanding: 'info',
          Pending: 'info',
        },
        defaultVariant: 'info',
      },
    },
    { field: 'grantName', header: 'Grant Name', type: 'text', sortable: true, width: 150, align: 'center', visibility: 'picker' },
    { field: 'planName', header: 'Plan Name', type: 'text', sortable: false, width: 150, visibility: 'picker' },
    {
      field: 'exercisePrice',
      header: 'Exercise Price',
      type: 'currency',
      sortable: false,
      width: 150,
      align: 'center',
      format: { decimals: 2 },
    },
    { field: 'exercisableUnits', header: 'Exercisable Units', type: 'number', sortable: false, width: 150, align: 'right' },
    { field: 'unitsGranted', header: 'Awarded Units', type: 'number', sortable: true, width: 150, align: 'center', visibility: 'picker' },
    { field: 'vestedUnits', header: 'Vested Units', type: 'number', sortable: false, width: 150, align: 'center', visibility: 'picker' },
    { field: 'exercisedUnit', header: 'Exercised Units', type: 'number', sortable: true, width: 180, align: 'center', visibility: 'picker' },
    { field: 'cancelledUnits', header: 'Canceled Units', type: 'number', sortable: true, width: 180, align: 'center', visibility: 'picker' },
    { field: 'outStandingUnits', header: 'Outstanding Units', type: 'number', sortable: true, width: 180, align: 'center', visibility: 'picker' },
    { field: 'unvestedUnit', header: 'Unvested Units', type: 'number', sortable: false, width: 150, align: 'center' },
    { field: 'expiredUnits', header: 'Expired Units', type: 'number', sortable: true, width: 180, align: 'center', visibility: 'picker' },
    { field: 'expirationDate', header: 'Expiration Date', type: 'date', sortable: false, width: 180, align: 'center', visibility: 'picker' },
    { field: 'outstandingPrice', header: 'Outstanding Value', type: 'text', sortable: false, width: 180, align: 'center', visibility: 'picker' },
    { field: 'vestingSchedule', header: 'Vesting Schedule', type: 'text', sortable: true, width: 180, align: 'center', visibility: 'picker' },
    { field: 'nextVestingDate', header: 'Next Vesting Date', type: 'date', sortable: false, width: 180, align: 'center', visibility: 'picker' },
    {
      field: 'isAwardPublished',
      header: 'Publish Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      conditionalValues: { true: 'PUBLISHED', false: 'NOT PUBLISHED' },
      badge: { statusMap: { true: 'success', false: 'info' }, defaultVariant: 'info' },
    },
    {
      field: 'status',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      badge: {
        statusMap: { Approved: 'success', Cancelled: 'error', Rejected: 'error', Pending: 'info' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'isAccepted',
      header: 'Accept Status',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      visibility: 'picker',
      conditionalValues: { true: 'ACCEPTED', false: 'NEEDS ACCEPTANCE' },
      badge: { statusMap: { true: 'success', false: 'error' }, defaultVariant: 'error' },
    },
    {
      field: 'acceptanceMethodName',
      header: 'Acceptance Method',
      type: 'badge',
      sortable: true,
      width: 180,
      align: 'center',
      conditionalValues: { Online: 'ONLINE', Offline: 'OFFLINE' },
      badge: { statusMap: { Online: 'success', Offline: 'error' }, defaultVariant: 'default' },
    },
  ],

  actions: {
    row: [
      { id: 'SUMMARY_OPTION_AWARDS', label: 'Summary' },
      { id: 'VIEW_OPTION_AWARDS', label: 'View' },
      { id: 'EDIT_OPTION_AWARDS', label: 'Edit' },
      { id: 'DELETE_OPTION_AWARDS', label: 'Delete', variant: 'danger' },
      { id: 'CANCEL_OPTION_AWARDS', label: 'Cancel', variant: 'danger' },
      { id: 'PUBLISH_AWARD_OPTION_AWARDS', label: 'Publish Award' },
      { id: 'VIEW_COMMENTS_OPTION_AWARDS', label: 'View Comments' },
      { id: 'VIEW_ACCEPTANCE_OPTION_AWARDS', label: 'View Acceptance' },
    ],
    bulk: [
      { id: 'BULK_PUBLISH_OPTION_AWARDS', label: 'Publish Award' },
      { id: 'BULK_DELETE_OPTION_AWARDS', label: 'Bulk Delete', variant: 'danger' },
      { id: 'BULK_EXPORT_OPTION_AWARDS', label: 'Bulk Export' },
    ],
    toolbar: {
      primary: { id: 'GRANT_NEW_OPTION_OPTION_AWARDS', label: 'Grant New Option', icon: 'add_circle_outline' },
      secondary: [
        {
          label: 'Download Template',
          icon: 'download',
          type: 'download',
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.downloadTemplate,
          children: [{ id: 'DOWNLOAD_TEMPLATE_OPTION_AWARDS', label: 'Download Template' }],
        },
        {
          label: 'Import Bulk XLS',
          icon: 'upload',
          type: 'upload',
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importTemplate,
          children: [{ id: 'IMPORT_BULK_OPTION_AWARDS', label: 'Import Bulk XLS' }],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: { keyField: 'id', selectAllPages: true },
    columnPreferences: { persistenceKey: 'Option Admin', resizable: true, reorderable: true },
  },
};
